import React from 'react';
import { Box, Typography, Card, CardContent } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { SiteAnalytics } from '../../../types/analytics';
import { LandingPage } from '../../../types';

export interface AnalyticsCTAButtonTableProps {
  currentSite: LandingPage | undefined;
  analyticsData: SiteAnalytics | null;
  comparisonAnalyticsData: SiteAnalytics | null;
}

export const AnalyticsCTAButtonTable: React.FC<AnalyticsCTAButtonTableProps> = ({
  currentSite,
  analyticsData,
  comparisonAnalyticsData,
}) => {
  if (!currentSite?.settings?.ctaButtons || currentSite.settings.ctaButtons.length === 0) {
    return null;
  }

  return (
    <Box sx={{ mb: 3, px: 3 }}>
      <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#000000', mb: 2 }}>
        ボタン別クリック数 <span style={{ fontSize: '14px', fontWeight: 400 }}>全{currentSite.settings.ctaButtons.length}件</span>
      </Typography>
      <Box display="flex" gap={2} flexWrap="wrap">
        {currentSite.settings.ctaButtons.map((ctaButton: any, index: number) => {
          // CTAボタンIDごとのクリック数を取得
          const buttonId = ctaButton.id;
          const clicksPerButton = analyticsData?.metrics.ctaButtonClicks?.[buttonId] || 0;
          const comparisonClicksPerButton = comparisonAnalyticsData?.metrics.ctaButtonClicks?.[buttonId] || 0;
          const diff = clicksPerButton - comparisonClicksPerButton;

          // ボタン名を決定（実際のデータ構造に合わせて調整が必要）
          const buttonName = ctaButton.name || ctaButton.html || `ボタン${index + 1}`;
          // 簡易的に左/右ボタンやキャンペーンを判定（実際の実装では適切な判定が必要）
          let displayName = buttonName;
          if (ctaButton.position === 'left' || index === 0) {
            displayName = '左ボタン';
          } else if (ctaButton.position === 'right' || index === 1) {
            displayName = '右ボタン';
          } else {
            displayName = 'キャンペーン';
          }

          return (
            <Card key={index} sx={{ flex: '1 1 calc(25% - 16px)', minWidth: 200, maxWidth: 'calc(25% - 16px)', borderRadius: '12px', boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.1)' }}>
              <CardContent sx={{ p: 1.5 }}>
                <Typography sx={{ fontSize: '16px', color: '#000000', fontWeight: 600, mb: 1 }}>
                  {displayName}
                </Typography>
                <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                  {ctaButton.image ? (
                    <Box
                      component="img"
                      src={ctaButton.image}
                      alt={displayName}
                      sx={{
                        width: 48,
                        height: 48,
                        objectFit: 'contain',
                        borderRadius: '4px',
                        flexShrink: 0,
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 48,
                        height: 48,
                        borderRadius: '4px',
                        backgroundColor: index === 0 ? '#1B76D2' : index === 1 ? '#FF9800' : '#9E9E9E',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <DownloadIcon sx={{ fontSize: 24, color: '#FFFFFF' }} />
                    </Box>
                  )}
                  <Box display="flex" flexDirection="column" alignItems="flex-end" flex={1}>
                    <Typography variant="h4" component="div" sx={{ fontWeight: 600, lineHeight: 1.2, mb: -0.5 }}>
                      {clicksPerButton.toLocaleString()}
                    </Typography>
                    {(() => {
                      if (diff === 0) {
                        return (
                          <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                            <span style={{ color: '#9E9E9E' }}>(</span>
                            <span style={{ color: '#9E9E9E' }}>±0</span>
                            <span style={{ color: '#9E9E9E' }}>)</span>
                          </Typography>
                        );
                      } else if (diff > 0) {
                        return (
                          <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                            <span style={{ color: '#9E9E9E' }}>(</span>
                            <span style={{ color: '#FF9800' }}>↑+{Math.abs(diff)}</span>
                            <span style={{ color: '#9E9E9E' }}>)</span>
                          </Typography>
                        );
                      } else {
                        return (
                          <Typography sx={{ fontSize: '12px', lineHeight: 1.2 }}>
                            <span style={{ color: '#9E9E9E' }}>(</span>
                            <span style={{ color: '#1B76D2' }}>↓{diff}</span>
                            <span style={{ color: '#9E9E9E' }}>)</span>
                          </Typography>
                        );
                      }
                    })()}
                  </Box>
                </Box>
              </CardContent>
            </Card>
          );
        })}
      </Box>
    </Box>
  );
};

